import "./MasterTabStyle.css";
import {
  FiFolder, FiBarChart2, FiLayers, FiGitBranch,
  FiUsers, FiUserCheck, FiUser, FiEdit2, FiTrash2, FiPlus, FiSearch,
  FiTag, FiFlag, FiCheckCircle, FiBookOpen, FiServer, FiUserPlus, FiGrid, FiX,
  FiMenu
} from "react-icons/fi";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import API_BASE_URL from "./RestAPI.js";
import { showSuccessToast, showErrorToast } from "../constants/ToastService.js";
import SidePanel from "./SidePanel";

/* ───── Table column configs per master type ───── */
const TABLE_COLUMNS = {
  project: [
    { key: 'project_name', label: 'Project Name' },
    { key: 'project_description', label: 'Description', fallback: '-' },
    { key: 'project_manager_name', label: 'Manager' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  designation: [
    { key: 'designation_name', label: 'Designation' },
    { key: 'designation_prefix', label: 'Prefix' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  employee: [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'role_name', label: 'Designation' },
    { key: 'login_role_name', label: 'Login Role' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  loginRole: [
    { key: 'role_name', label: 'Role Name' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  ticketType: [
    { key: 'type_name', label: 'Ticket Type' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  priorityType: [
    { key: 'priority', label: 'Priority' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  status: [
    { key: 'ticket_status', label: 'Ticket Status' },
    { key: 'description', label: 'Description', fallback: '-' },
    { key: 'environment', label: 'Environment', fallback: '-' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  client: [
    { key: 'client_name', label: 'Client Name' },
    { key: 'client_email', label: 'Email' },
    { key: 'client_mobile_number', label: 'Mobile' },
    { key: 'project_name', label: 'Project' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  clientstatus: [
    { key: 'client_requirement_status', label: 'Requirement Status' },
    { key: 'description', label: 'Description', fallback: '-' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  impactarea: [
    { key: 'name', label: 'Impact Area' },
    { key: 'project_name', label: 'Project', fallback: '-' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  team: [
    { key: 'team_name', label: 'Team Name' },
    { key: 'team_lead_name', label: 'Team Lead' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  subModule: [
    { key: 'sub_module_name', label: 'Sub-Module' },
    { key: 'module_name', label: 'Module', fallback: 'Not assigned' },
    { key: 'short_name', label: 'Short Name', fallback: '-' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  module: [
    { key: 'module_name', label: 'Module' },
    { key: 'short_name', label: 'Short Name' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  environment: [
    { key: 'environment_name', label: 'Environment' },
    { key: 'short_name', label: 'Short Name' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  loginUser: [
    { key: 'employee_name', label: 'Employee' },
    { key: 'user_name', label: 'Username' },
    { key: 'email', label: 'Email' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
  gitlabrepo: [
    { key: 'repo_name', label: 'Repository Name' },
    { key: 'gitlab_project_id', label: 'GitLab ID' },
    { key: 'gitlab_project_path', label: 'Path', fallback: '-' },
    { key: 'repo_type', label: 'Type' },
    { key: 'project_name', label: 'Project' },
    { key: 'is_active', label: 'Status', type: 'status' },
  ],
};

/* ───── Searchable keys per type ───── */
const SEARCH_KEYS = {
  project: ['project_name', 'project_manager_name'],
  designation: ['designation_name', 'designation_prefix'],
  employee: ['name', 'email', 'role_name'],
  loginRole: ['role_name'],
  ticketType: ['type_name'],
  priorityType: ['priority'],
  status: ['ticket_status', 'environment'],
  client: ['client_name', 'client_email', 'project_name'],
  clientstatus: ['client_requirement_status'],
  impactarea: ['name', 'project_name'],
  team: ['team_name'],
  module: ['module_name'],
  subModule: ['sub_module_name', 'module_name'],
  environment: ['environment_name'],
  loginUser: ['employee_name', 'user_name', 'email'],
  gitlabrepo: ['repo_name', 'gitlab_project_path', 'repo_type', 'project_name'],
};

/* ───── Master categories & items ───── */
const MASTER_CATEGORIES = [
  {
    title: 'General',
    items: [
      { label: 'Projects', icon: FiFolder, name: 'Project', sidePanelName: 'project' },
      { label: 'Designations', icon: FiUserCheck, name: 'Designation', sidePanelName: 'designation' },
      { label: 'Login Role', icon: FiUsers, name: 'Login Role', sidePanelName: 'loginRole' },
      { label: 'Employees', icon: FiUser, name: 'Employee', sidePanelName: 'employee' },
    ],
  },
  {
    title: 'Tickets',
    items: [
      { label: 'Ticket Type', icon: FiTag, name: 'Ticket Type', sidePanelName: 'ticketType' },
      { label: 'Priority Type', icon: FiFlag, name: 'Priority Type', sidePanelName: 'priorityType' },
      { label: 'Ticket Status', icon: FiCheckCircle, name: 'Ticket Status', sidePanelName: 'status' },
      // Used by Quality (test cases, production bugs) and by tickets.
      { label: 'Modules', icon: FiGrid, name: 'Module', sidePanelName: 'module' },
      { label: 'Sub-Modules', icon: FiLayers, name: 'Sub-Module', sidePanelName: 'subModule' },
    ],
  },
  {
    title: 'Clients',
    items: [
      { label: 'Client', icon: FiUsers, name: 'Client', sidePanelName: 'client' },
      { label: 'Client Status', icon: FiBookOpen, name: 'Client Status', sidePanelName: 'clientstatus' },
    ],
  },
  {
    title: 'AI & Artifacts',
    items: [
      { label: 'Impact Areas', icon: FiLayers, name: 'Impact Area', sidePanelName: 'impactarea' },
    ],
  },
  {
    title: 'Integrations',
    items: [
      { label: 'GitLab Repositories', icon: FiGitBranch, name: 'GitLab Repo', sidePanelName: 'gitlabrepo' },
    ],
  },
];

/* ───── Fetch URL map ───── */
const FETCH_URLS = {
  Projects: `core/project/`,
  Designations: `core/designation/`,
  Employees: `core/employee/`,
  Team: `core/team/`,
  'Ticket Status': `core/status/`,
  Client: `client_requirements/client/`,
  'Client Status': `client_requirements/clientstatus/`,
  'Ticket Type': `core/ticketType/`,
  'Priority Type': `core/priorityType/`,
  Module: `core/module/`,
  Modules: `core/module/`,
  'Sub-Modules': `core/sub-module/`,
  Environment: `core/environment/`,
  'Login Role': `core/loginRole/`,
  'Login Users': `core/loginUser/`,
  'Impact Areas': `client_requirements/impact-areas/`,
  'GitLab Repositories': `ticketing/gitlab/repos/`,
};

const ROWS_PER_PAGE = 12;

/* ───── Status Badge ───── */
const StatusBadge = ({ active }) => (
  <span className={`mst-status ${active ? 'mst-status--active' : 'mst-status--inactive'}`}>
    <span className="mst-status__dot" />
    {active ? 'Active' : 'Inactive'}
  </span>
);

/* ───── Pagination ───── */
const Pagination = ({ total, page, setPage }) => {
  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  if (totalPages <= 1) return null;

  const start = (page - 1) * ROWS_PER_PAGE + 1;
  const end = Math.min(page * ROWS_PER_PAGE, total);

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }

  return (
    <div className="mst-pagination">
      <span className="mst-pagination__info">{start}–{end} of {total}</span>
      <div className="mst-pagination__controls">
        <button className="mst-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
        {pages.map((p, i) =>
          p === '...' ? <span key={`e${i}`} className="mst-page-ellipsis">...</span> :
            <button key={p} className={`mst-page-btn ${p === page ? 'mst-page-btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
        )}
        <button className="mst-page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
      </div>
    </div>
  );
};

/* ═══════════════ Project Editor — Full-screen split layout ═══════════════ */
const ProjectEditor = ({ editData, onClose }) => {
  const [form, setForm] = useState({
    project_name: editData?.project_name || '',
    project_description: editData?.project_description || '',
    project_manager_id: editData?.project_manager_id || '',
    is_active: editData?.is_active !== undefined ? editData.is_active : true,
  });
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lineCount, setLineCount] = useState(1);
  const textareaRef = useRef(null);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` };

  useEffect(() => {
    fetch(`${API_BASE_URL}core/employee/`, { method: 'GET', headers })
      .then(r => r.json()).then(d => { if (d.status === 'success') setEmployees(d.data); }).catch(() => {});
  }, []);

  useEffect(() => {
    setLineCount((form.project_description || '').split('\n').length);
  }, [form.project_description]);

  const handleSave = async () => {
    if (!form.project_name.trim()) { showErrorToast('Project name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editData?.id) payload.id = editData.id;
      const res = await fetch(`${API_BASE_URL}core/project/`, {
        method: editData?.id ? 'PUT' : 'POST', headers, body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.status === 'success') { showSuccessToast(editData?.id ? 'Project updated' : 'Project created'); onClose(); }
      else showErrorToast(data.message || 'Save failed');
    } catch (e) { showErrorToast('Save failed'); }
    finally { setSaving(false); }
  };

  const wordCount = (form.project_description || '').trim().split(/\s+/).filter(Boolean).length;
  const charCount = (form.project_description || '').length;

  return (
    <div className="proj-editor-overlay">
      <div className="proj-editor">
        {/* Top bar */}
        <div className="proj-editor__topbar">
          <h2>{editData?.id ? 'Edit Project' : 'New Project'}</h2>
          <div className="proj-editor__topbar-right">
            <span className="proj-editor__stats">{lineCount} lines · {wordCount} words · {charCount} chars</span>
            <button className="proj-editor__cancel" onClick={onClose}>Cancel</button>
            <button className="proj-editor__save" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Project'}</button>
          </div>
        </div>

        {/* Split layout */}
        <div className="proj-editor__body">
          {/* Left: Settings */}
          <div className="proj-editor__settings">
            <h3>Project Settings</h3>
            <div className="proj-editor__field">
              <label>Project Name *</label>
              <input type="text" value={form.project_name} onChange={e => setForm(p => ({ ...p, project_name: e.target.value }))} placeholder="Enter project name" />
            </div>
            <div className="proj-editor__field">
              <label>Project Manager</label>
              <select value={form.project_manager_id || ''} onChange={e => setForm(p => ({ ...p, project_manager_id: e.target.value }))}>
                <option value="" disabled>Select Manager</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div className="proj-editor__field">
              <label className="proj-editor__checkbox">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
                Active
              </label>
            </div>
            <div className="proj-editor__hint">
              <h4>About Description</h4>
              <p>The project description serves as a <strong>system prompt</strong> for AI artifact generation. Write detailed context about your project including:</p>
              <ul>
                <li>Tech stack and architecture</li>
                <li>Business domain and rules</li>
                <li>Coding conventions</li>
                <li>API patterns and standards</li>
                <li>UI/UX guidelines</li>
                <li>Testing requirements</li>
              </ul>
            </div>
          </div>

          {/* Right: Full-height editor */}
          <div className="proj-editor__editor">
            <div className="proj-editor__editor-header">
              <span>Project Description</span>
              <span className="proj-editor__editor-hint">This content is used as context for AI artifact generation</span>
            </div>
            <div className="proj-editor__editor-wrap">
              <div className="proj-editor__line-nums">
                {Array.from({ length: Math.max(lineCount, 30) }, (_, i) => (
                  <div key={i} className="proj-editor__line-num">{i + 1}</div>
                ))}
              </div>
              <textarea
                ref={textareaRef}
                className="proj-editor__textarea"
                value={form.project_description}
                onChange={e => setForm(p => ({ ...p, project_description: e.target.value }))}
                placeholder="Describe your project in detail...&#10;&#10;This description will be used as the system prompt when generating artifacts (dev specs, QA plans, unit tests) from client requirements.&#10;&#10;Include: tech stack, architecture, domain rules, coding standards, API patterns, UI conventions, testing requirements..."
                spellCheck={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════ Main Component ═══════════════ */
const MasterView = () => {
  const [searchParams] = useSearchParams();
  const [activeItem, setActiveItem] = useState(null);
  const [masterData, setMasterData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showProjectEditor, setShowProjectEditor] = useState(false);
  const [panelType, setPanelType] = useState("");
  const [refreshData, setRefreshData] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [navSearch, setNavSearch] = useState('');
  const searchInputRef = useRef(null);
  const [recordCounts, setRecordCounts] = useState({});
  // Drag-to-reorder state — used only on the Ticket Status master
  const [draggedId, setDraggedId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);

  const headers = {
    "Content-Type": "application/json",
    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
  };

  /* ───── Fetch data ───── */
  const fetchMaster = useCallback(async (label) => {
    const url = FETCH_URLS[label];
    if (!url) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}${url}`, { method: 'GET', headers });
      const data = await res.json();
      if (data.status === "success") {
        const arr = Array.isArray(data.data) ? data.data : [];
        setMasterData(arr);
        setRecordCounts(prev => ({ ...prev, [label]: arr.length }));
      } else {
        showErrorToast(data.message || 'Failed to load');
        setMasterData([]);
      }
    } catch (err) {
      console.error(err);
      setMasterData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ───── Preload counts for all masters on mount ───── */
  useEffect(() => {
    const loadCounts = async () => {
      const entries = Object.entries(FETCH_URLS);
      const counts = {};
      await Promise.allSettled(
        entries.map(async ([label, url]) => {
          try {
            const res = await fetch(`${API_BASE_URL}${url}`, { method: 'GET', headers });
            const data = await res.json();
            if (data.status === "success") {
              counts[label] = Array.isArray(data.data) ? data.data.length : 0;
            }
          } catch { /* ignore */ }
        })
      );
      setRecordCounts(counts);
    };
    loadCounts();
  }, []);

  /* ───── Select a master item ───── */
  const handleItemClick = (item) => {
    setActiveItem(item);
    setSearch('');
    setPage(1);
    setIsSidebarOpen(false);
    fetchMaster(item.label);
  };

  /* Auto-select from URL param or first item on mount */
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const allItems = MASTER_CATEGORIES.flatMap(c => c.items);
      const match = allItems.find(item => item.label === tabParam);
      if (match) { handleItemClick(match); return; }
    }
    if (!activeItem && MASTER_CATEGORIES.length > 0) {
      handleItemClick(MASTER_CATEGORIES[0].items[0]);
    }
  }, [searchParams]);

  const refreshTable = () => {
    if (activeItem) fetchMaster(activeItem.label);
  };

  const handleAdd = () => {
    setEditData(null);
    setPanelType(activeItem.sidePanelName);
    if (activeItem.sidePanelName === 'project') { setShowProjectEditor(true); return; }
    setIsSidebarOpen(true);
  };

  const handleEdit = (data) => {
    setEditData(data);
    setPanelType(activeItem.sidePanelName);
    if (activeItem.sidePanelName === 'project') { setShowProjectEditor(true); return; }
    setIsSidebarOpen(true);
  };

  const handleClosePanel = () => {
    refreshTable();
    setIsSidebarOpen(false);
  };

  const handleDelete = async (id) => {
    const type = activeItem.sidePanelName;
    let url;
    if (type === 'impactarea') {
      url = `${API_BASE_URL}client_requirements/impact-areas/?id=${id}`;
    } else if (type === 'gitlabrepo') {
      url = `${API_BASE_URL}ticketing/gitlab/repos/?id=${id}`;
    } else if (type === 'client' || type === 'clientstatus') {
      url = `${API_BASE_URL}client_requirements/${type}/?id=${id}`;
    } else if (type === 'subModule') {
      url = `${API_BASE_URL}core/sub-module/?id=${id}`;
    } else {
      url = `${API_BASE_URL}core/${type}/?id=${id}&company_id=1`;
    }
    try {
      const res = await fetch(url, { method: 'DELETE', headers });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(data.message);
        refreshTable();
      } else {
        showErrorToast(data.message);
      }
    } catch {
      showErrorToast("Delete failed");
    }
  };

  /* ───── Keyboard shortcut: Ctrl+K to focus search ───── */
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  /* ───── Filtered nav items ───── */
  const filteredCategories = useMemo(() => {
    if (!navSearch.trim()) return MASTER_CATEGORIES;
    const q = navSearch.toLowerCase();
    return MASTER_CATEGORIES.map(cat => ({
      ...cat,
      items: cat.items.filter(item => item.label.toLowerCase().includes(q)),
    })).filter(cat => cat.items.length > 0);
  }, [navSearch]);

  /* ───── Filtered + paginated table data ───── */
  const sidePanelType = activeItem?.sidePanelName || '';
  const columns = TABLE_COLUMNS[sidePanelType] || [];

  const filteredData = useMemo(() => {
    if (!search.trim()) return masterData;
    const q = search.toLowerCase();
    const keys = SEARCH_KEYS[sidePanelType] || [];
    return masterData.filter(row =>
      keys.some(k => String(row[k] || '').toLowerCase().includes(q))
    );
  }, [masterData, search, sidePanelType]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return filteredData.slice(start, start + ROWS_PER_PAGE);
  }, [filteredData, page]);

  const renderCell = (row, col) => {
    if (col.type === 'status') return <StatusBadge active={row[col.key]} />;
    const val = row[col.key];
    if (val === null || val === undefined || val === '') return col.fallback || '-';
    return String(val);
  };

  const activeCount = filteredData.length;
  const totalCount = masterData.length;

  // ── Drag-to-reorder (Ticket Status only) ──────────────────────────────
  const isStatusMaster = sidePanelType === 'status';
  // Ticket Status shows every row (no pagination); other masters stay paginated
  const displayData = isStatusMaster ? filteredData : paginatedData;
  // Disable drag when filtered — reorder over a subset is ambiguous
  const canDrag = isStatusMaster && !search.trim() && filteredData.length === masterData.length;

  const persistStatusOrder = async (orderedIds) => {
    try {
      const res = await fetch(`${API_BASE_URL}core/status/`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ order: orderedIds, company_id: 1 }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        showSuccessToast('Status order updated');
        refreshTable();
      } else {
        showErrorToast(data.message || 'Failed to reorder');
      }
    } catch {
      showErrorToast('Failed to reorder');
    }
  };

  const handleRowDragStart = (id) => setDraggedId(id);
  const handleRowDragOver = (e, id) => { e.preventDefault(); setDropTargetId(id); };
  const handleRowDragLeave = () => setDropTargetId(null);
  const handleRowDrop = (e, targetId) => {
    e.preventDefault();
    if (draggedId == null || draggedId === targetId) {
      setDraggedId(null); setDropTargetId(null);
      return;
    }
    const fromIdx = masterData.findIndex(r => r.id === draggedId);
    const toIdx = masterData.findIndex(r => r.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = masterData.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setMasterData(next);
    setDraggedId(null); setDropTargetId(null);
    persistStatusOrder(next.map(r => r.id));
  };
  const handleRowDragEnd = () => { setDraggedId(null); setDropTargetId(null); };

  /* ═══════════════ JSX ═══════════════ */
  return (
    <div className="mst-layout mst-layout--full">
      {/* ── Content Panel (full width — sidebar handles navigation) ── */}
      {activeItem ? (
        <>
          {/* ── Header ── */}
          <div className="mst-page-header">
            <div className="mst-page-header__left">
              <h1 className="mst-page-header__title">{activeItem.label}</h1>
              <span className="mst-page-header__count">{totalCount} record{totalCount !== 1 ? 's' : ''}</span>
            </div>
            <button className="mst-page-header__add" onClick={handleAdd}>
              <FiPlus size={14} /> Add {activeItem.name}
            </button>
          </div>

          {/* ── Toolbar (connected to table) ── */}
          <div className="mst-page-toolbar">
            <span className="mst-page-toolbar__count">{activeCount} result{activeCount !== 1 ? 's' : ''}</span>
            <div className="mst-page-toolbar__search">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={`Search ${activeItem.label.toLowerCase()}...`}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
              <FiSearch size={14} />
            </div>
          </div>

          {/* ── Table Card ── */}
          <div className="mst-page-table-card">
            {loading ? (
              <div className="mst-loading">
                <div className="mst-loading__spinner" />
                <span>Loading...</span>
              </div>
            ) : (
              <table className="mst-page-table">
                <thead>
                  <tr>
                    {isStatusMaster && <th style={{ width: 32 }} title="Drag to reorder"></th>}
                    <th style={{ width: 44 }}>#</th>
                    {columns.map(col => <th key={col.key}>{col.label}</th>)}
                    <th style={{ width: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayData.length > 0 ? displayData.map((row, idx) => {
                    const isDragging = draggedId === row.id;
                    const isDropTarget = dropTargetId === row.id && draggedId !== row.id;
                    return (
                      <tr
                        key={row.id || idx}
                        className={`mst-page-row${isDragging ? ' mst-page-row--dragging' : ''}${isDropTarget ? ' mst-page-row--drop-target' : ''}`}
                        draggable={canDrag}
                        onDragStart={canDrag ? () => handleRowDragStart(row.id) : undefined}
                        onDragOver={canDrag ? (e) => handleRowDragOver(e, row.id) : undefined}
                        onDragLeave={canDrag ? handleRowDragLeave : undefined}
                        onDrop={canDrag ? (e) => handleRowDrop(e, row.id) : undefined}
                        onDragEnd={canDrag ? handleRowDragEnd : undefined}
                      >
                        {isStatusMaster && (
                          <td className="mst-page-td--drag" title={canDrag ? 'Drag to reorder' : 'Clear search/filter to enable reordering'}>
                            <FiMenu size={14} style={{ opacity: canDrag ? 0.75 : 0.25, cursor: canDrag ? 'grab' : 'not-allowed' }} />
                          </td>
                        )}
                        <td className="mst-page-td--num">{isStatusMaster ? idx + 1 : (page - 1) * ROWS_PER_PAGE + idx + 1}</td>
                        {columns.map(col => <td key={col.key}>{renderCell(row, col)}</td>)}
                        <td>
                          <div className="mst-page-actions">
                            <button className="fd-action-btn" onClick={() => handleEdit(row)} title="Edit">
                              <FiEdit2 size={13} />
                            </button>
                            <button className="fd-action-btn fd-action-btn--danger" onClick={() => handleDelete(row.id)} title="Delete">
                              <FiTrash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={columns.length + 2 + (isStatusMaster ? 1 : 0)} className="mst-page-empty">
                        {search ? `No results for "${search}"` : 'No records found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Pagination — hidden for Ticket Status (shows all rows) */}
            {!isStatusMaster && <Pagination total={filteredData.length} page={page} setPage={setPage} />}
          </div>
        </>
      ) : (
        <div className="mst-placeholder">
          <FiGrid size={36} />
          <p>Select a master from the sidebar to get started</p>
        </div>
      )}

      {/* ── Project Editor (Full-screen split) ── */}
      {showProjectEditor && (
        <ProjectEditor
          editData={editData}
          onClose={() => { setShowProjectEditor(false); refreshTable(); }}
        />
      )}

      {/* ── Modern Slide-over Panel ── */}
      {isSidebarOpen && (
        <div className="mst-panel-overlay" onClick={handleClosePanel}>
          <div className="mst-panel" onClick={e => e.stopPropagation()}>
            <div className="mst-panel__header">
              <h2>{editData ? `Edit ${activeItem?.name}` : `Add ${activeItem?.name}`}</h2>
              <button className="mst-panel__close" onClick={handleClosePanel}><FiX size={18} /></button>
            </div>
            <div className="mst-panel__body">
              <SidePanel
                closePanel={handleClosePanel}
                editData={editData}
                panelType={panelType}
                setRefreshData={setRefreshData}
                buttonType={activeItem?.name}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterView;
